import React, { useState } from 'react';
import { X, Database, Globe, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Flag, Send } from 'lucide-react';
import { useSystemStatus } from '../hooks/useSystemStatus';

interface SystemStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SystemStatusModal({ isOpen, onClose }: SystemStatusModalProps) {
  const { status, refreshStatus } = useSystemStatus();
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportData, setReportData] = useState({
    service: 'api' as 'api' | 'database' | 'general',
    description: '',
    severity: 'medium' as 'low' | 'medium' | 'high'
  });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);

  if (!isOpen) return null;

  const getStatusIcon = (statusValue: 'online' | 'offline' | 'checking') => {
    switch (statusValue) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'offline':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'checking':
        return <RefreshCw className="w-5 h-5 text-blue-500 animate-spin" />;
    }
  };

  const getStatusColor = (statusValue: 'online' | 'offline' | 'checking') => {
    switch (statusValue) {
      case 'online':
        return 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/50';
      case 'offline':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50';
      case 'checking':
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/50';
    }
  };

  const getStatusTextColor = (statusValue: 'online' | 'offline' | 'checking') => {
    switch (statusValue) {
      case 'online':
        return 'text-emerald-700 dark:text-emerald-400';
      case 'offline':
        return 'text-red-700 dark:text-red-400';
      case 'checking':
        return 'text-blue-700 dark:text-blue-400';
    }
  };

  const getApiDescription = () => {
    switch (status.api) {
      case 'online':
        return 'REST API is operational and responding to requests. All device status checks and operations are functioning normally.';
      case 'offline':
        return 'REST API is currently unavailable. Device status checks and remote operations may not work properly.';
      case 'checking':
        return 'Currently testing API connectivity and response times. Please wait for status update.';
    }
  };

  const getDatabaseDescription = () => {
    switch (status.database) {
      case 'online':
        return 'Database connection is stable and all queries are executing successfully. Device data is being stored and retrieved properly.';
      case 'offline':
        return 'Database connection is down. Device data cannot be saved or retrieved. Please check your connection.';
      case 'checking':
        return 'Testing database connectivity and query performance. Verifying data integrity and access permissions.';
    }
  };

  const getLastSyncDescription = () => {
    if (!status.lastSync) {
      return 'System status has never been checked. Click refresh to perform initial health check.';
    }
    const timeDiff = Date.now() - status.lastSync.getTime();
    const minutes = Math.floor(timeDiff / 60000);
    if (minutes < 1) {
      return 'System status was just updated. All components are being monitored in real-time.';
    } else if (minutes < 5) {
      return `Last checked ${minutes} minute${minutes > 1 ? 's' : ''} ago. Status is current and reliable.`;
    } else {
      return `Last checked ${minutes} minutes ago. Consider refreshing for the most current status.`;
    }
  };

  const handleReportOutage = async () => {
    setSubmittingReport(true);
    
    // Simulate API call to report outage
    await new Promise(resolve => setTimeout(resolve, 1500));

    // If I chose to deploy this feature this would trigger a power automate endpoint that sends me a teams message
    console.log('Outage report submitted:', {
      ...reportData,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      currentStatus: status
    });
    
    setSubmittingReport(false);
    setReportSubmitted(true);
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setShowReportForm(false);
      setReportSubmitted(false);
      setReportData({
        service: 'api',
        description: '',
        severity: 'medium'
      });
    }, 3000);
  };

  const overallStatus = status.database === 'online' && status.api === 'online' ? 'online' : 
                      status.database === 'checking' || status.api === 'checking' ? 'checking' : 'offline';

  const getOverallStatusBg = () => {
    switch (overallStatus) {
      case 'online':
        return 'bg-emerald-100 dark:bg-emerald-900/30';
      case 'offline':
        return 'bg-red-100 dark:bg-red-900/30';
      case 'checking':
        return 'bg-blue-100 dark:bg-blue-900/30';
    }
  };

  return (
    <div className="modal-overlay">
      <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-lg border border-slate-200/60 dark:border-slate-700/60 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200/60 dark:border-slate-700/60 bg-gradient-to-r from-slate-50/80 to-slate-100/80 dark:from-slate-800/80 dark:to-slate-700/80">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getOverallStatusBg()}`}>
              {getStatusIcon(overallStatus)}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">System Status</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">Real-time system monitoring</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-200/60 dark:hover:bg-slate-600/60 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {!showReportForm ? (
            <>
              {/* REST API Status */}
              <div className={`p-4 rounded-xl border ${getStatusColor(status.api)}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700/50 rounded-lg flex items-center justify-center">
                      <Globe className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-white">REST API Endpoint</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Supabase Edge Functions</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(status.api)}
                    <span className={`text-sm font-medium capitalize ${getStatusTextColor(status.api)}`}>
                      {status.api}
                    </span>
                  </div>
                </div>
                <p className={`text-sm ${getStatusTextColor(status.api)} leading-relaxed`}>
                  {getApiDescription()}
                </p>
              </div>

              {/* Database Status */}
              <div className={`p-4 rounded-xl border ${getStatusColor(status.database)}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-slate-100 dark:bg-slate-700/50 rounded-lg flex items-center justify-center">
                      <Database className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-white">Database Connection</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Supabase PostgreSQL</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(status.database)}
                    <span className={`text-sm font-medium capitalize ${getStatusTextColor(status.database)}`}>
                      {status.database}
                    </span>
                  </div>
                </div>
                <p className={`text-sm ${getStatusTextColor(status.database)} leading-relaxed`}>
                  {getDatabaseDescription()}
                </p>
              </div>

              {/* Last Sync */}
              <div className="p-4 bg-slate-50/80 dark:bg-slate-700/50 rounded-xl border border-slate-200/60 dark:border-slate-600/60">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-slate-100 dark:bg-slate-600/50 rounded-lg flex items-center justify-center">
                      <Clock className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-900 dark:text-white">Last Status Check</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        {status.lastSync ? status.lastSync.toLocaleString() : 'Never'}
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                  {getLastSyncDescription()}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={refreshStatus}
                  className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh Status</span>
                </button>
                
                <button
                  onClick={() => setShowReportForm(true)}
                  className="flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-4 py-3 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
                  title="Report an issue if you're experiencing problems despite the status showing as online"
                >
                  <Flag className="w-4 h-4" />
                  <span>Report Issue</span>
                </button>
              </div>
            </>
          ) : (
            /* Report Form */
            <div className="space-y-4">
              {!reportSubmitted ? (
                <>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                      <Flag className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 dark:text-white">Report System Issue</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">Help us identify and resolve problems</p>
                    </div>
                  </div>

                  {/* Service Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Which service is experiencing issues?
                    </label>
                    <select
                      value={reportData.service}
                      onChange={(e) => setReportData(prev => ({ ...prev, service: e.target.value as 'api' | 'database' | 'general' }))}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                    >
                      <option value="api">REST API / Edge Functions</option>
                      <option value="database">Database Connection</option>
                      <option value="general">General System Issues</option>
                    </select>
                  </div>

                  {/* Severity Selection */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Issue Severity
                    </label>
                    <div className="flex space-x-3">
                      {[
                        { value: 'low', label: 'Low', color: 'from-green-500 to-emerald-600' },
                        { value: 'medium', label: 'Medium', color: 'from-amber-500 to-orange-600' },
                        { value: 'high', label: 'High', color: 'from-red-500 to-red-600' }
                      ].map((severity) => (
                        <button
                          key={severity.value}
                          type="button"
                          onClick={() => setReportData(prev => ({ ...prev, severity: severity.value as 'low' | 'medium' | 'high' }))}
                          className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all duration-200 text-sm font-medium ${
                            reportData.severity === severity.value
                              ? `bg-gradient-to-r ${severity.color} text-white border-transparent shadow-lg`
                              : 'border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'
                          }`}
                        >
                          {severity.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Describe the issue you're experiencing
                    </label>
                    <textarea
                      value={reportData.description}
                      onChange={(e) => setReportData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Please describe what's not working, error messages you're seeing, or any other relevant details..."
                      rows={4}
                      className="w-full px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-200 bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 resize-none"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-2">
                    <button
                      onClick={() => setShowReportForm(false)}
                      className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-all duration-200"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReportOutage}
                      disabled={!reportData.description.trim() || submittingReport}
                      className="flex-1 flex items-center justify-center space-x-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-4 py-2 rounded-lg transition-all duration-200 disabled:opacity-50 shadow-lg hover:shadow-xl"
                    >
                      {submittingReport ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          <span>Submitting...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Submit Report</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                /* Success Message */
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Report Submitted</h3>
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    BETA: Thank you for reporting this issue. Cody Manning has been notified and will investigate the problem.
                  </p>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-lg p-3">
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">
                      Report ID: #{Math.random().toString(36).substr(2, 9).toUpperCase()}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
